import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminAddonsService } from '../services/admin.addons.service'
import type { CatalogAddonDocument } from '@/types'
import type { AddonFormValues } from '../types'

interface UseAdminAddonsResult {
  addons: Array<CatalogAddonDocument & { id: string }>
  loading: boolean
  error: string | null
  createAddon: (data: AddonFormValues) => Promise<string>
  updateAddon: (id: string, data: Partial<AddonFormValues>) => Promise<void>
  deleteAddon: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminAddons(): UseAdminAddonsResult {
  const [addons, setAddons] = useState<Array<CatalogAddonDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminAddonsService
      .getAllAddons()
      .then(setAddons)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load add-ons'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createAddon = useCallback(async (data: AddonFormValues) => {
    const id = await adminAddonsService.createAddon(data)
    refresh()
    return id
  }, [refresh])

  const updateAddon = useCallback(async (id: string, data: Partial<AddonFormValues>) => {
    await adminAddonsService.updateAddon(id, data)
    refresh()
  }, [refresh])

  const deleteAddon = useCallback(async (id: string) => {
    await adminAddonsService.deleteAddon(id)
    refresh()
  }, [refresh])

  return { addons, loading, error, createAddon, updateAddon, deleteAddon, refresh }
}
